// React Dependencies
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router"
import { useSelector } from "react-redux"

// External Dependencies
import { useTranslation } from "react-i18next"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import SVG from "react-inlinesvg"

// Internal Dependencies
import SubMenuFilter from "../SubMenuFilter.jsx"
import { EmptyCard, TableSkeleton, TableView } from "../../../../../component/index.jsx"
import { PRIVILEGE_ENUM } from "../../../../../constants/PrivilegeConstants.js"
import { DetailSvgIcon } from "../../../../../component/atoms/Icons/SVG/index.jsx"
import { formatterRowComponent } from "../../../../../component/atoms/Tabel/index.jsx"
import { RoutesConstants } from "../../../../../constants/RoutesConstants.js"
import { loadUserLogin } from "../../../../service/userManagementAction.js"
import { useSubheader } from "../../../../../_metronic/layout/index.js"
import { sortCaret, toAbsoluteUrl } from "../../../../../_metronic/_helpers/index.js"
import { getUrlParsingPageFilter } from "../../../../../config/EndpointCollection.js"
import { getKeyFromValue } from "../../../../helpers/Utils.js"
import { stringFormatting, toMeter, toRupiah } from "../../../../helpers/TextHelper.js"
import {
  AREA_REGISTERED_ACTIVE_TAB_ENUM, AREA_REGISTERED_ENUM,
  DATE_TYPE_ENUM,
  LABELING_CONST,
  isFrozen
} from "../../../../../constants/StringConstant.js"
import { ICON_CONSTANTS, ILLUSTRATION_CONSTANTS } from "../../../../../constants/ImageConstants.js"
import ModalListBA from "../ModalListBA.jsx"
import {
  getCountDownToday,
  getHSTUnit,
  getTonnageUnit
} from "../../../../helpers/StringCollection.js"
import { timeStampToDate } from "../../../../helpers/DateHelper.js"
import { AREA } from "../../../../../constants/InitTypeConstants.js"

export const ListActive = ({
  data,
  changePage,
  history,
  general,
  initialFilterTable,
  isActiveSearch,
  filterOn,
  handleChangeSubTab,
  initialActive,
  isMultiSelect = false,
  actionType = null,
  onSelectionChange,
  initialSelectedItems = []
}) => {
  const privileges = loadUserLogin().privilege
  const location = useLocation()
  const subheader = useSubheader()
  const { t } = useTranslation()
  const { General } = useSelector((state) => state)
  const [showModalListBA, setShowModalListBA] = useState(false)
  const [selectedAreaId, setSelectedAreaId] = useState(null)
  const [selectedRows, setSelectedRows] = useState(initialSelectedItems)
  const [selectedIds, setSelectedIds] = useState(() => initialSelectedItems.map((e) => e?.land_id))

  const handleSelect = (row, isSelected) => {
    let rows = [...selectedRows]
    if (isSelected) {
      rows.push(row)
    } else {
      rows = rows.filter((e) => e?.land_id !== row?.land_id)
    }
    setSelectedRows(rows)
    setSelectedIds(rows.map((e) => e?.land_id))
    onSelectionChange?.(rows)
  }

  const handleSelectAll = (isSelect, rows) => {
    let current = [...selectedRows]
    if (isSelect) {
      rows?.forEach((row) => {
        if (!current.find((e) => e?.land_id === row?.land_id)) {
          current.push(row)
        }
      })
    } else {
      rows?.forEach((row) => {
        current = current.filter((e) => e?.land_id !== row?.land_id)
      })
    }
    setSelectedRows(current)
    setSelectedIds(current.map((e) => e?.land_id))
    onSelectionChange?.(current)
  }
  const [subTabActive, setSubTabActive] = useState(
    getKeyFromValue(
      AREA_REGISTERED_ACTIVE_TAB_ENUM.NOT_PLANTED_YET,
      AREA_REGISTERED_ACTIVE_TAB_ENUM
    )
  )

  const handleOnClickReportDetail = (id) => {
    setShowModalListBA(true)
    setSelectedAreaId(id)
  }

  useEffect(() => {
    subheader.setButton(undefined)
  }, [])

  const mountedRef = useRef(false)
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      return
    }
    setSelectedRows([])
    setSelectedIds([])
    onSelectionChange?.([])
  }, [isMultiSelect, actionType])

  const nonSelectableIds = useMemo(() => {
    if (!actionType || !data?.pageData) return []
    if (actionType === LABELING_CONST.FREEZE || actionType === LABELING_CONST.BLACKLIST) {
      return data.pageData
        .filter((row) => isFrozen(row.area_status) || row.area_status === LABELING_CONST.BLACKLISTED)
        .map((row) => row.land_id)
    }
    if (actionType === LABELING_CONST.UNFREEZE) {
      return data.pageData
        .filter((row) => !isFrozen(row.area_status))
        .map((row) => row.land_id)
    }
    return []
  }, [actionType, data?.pageData])

  useEffect(() => {
    if (initialActive) {
      setSubTabActive(initialActive)
    }
  }, [initialActive])

  const uiCode = (e) => {
    return (
      <>
        <div className="d-flex align-items-start mb-1">
          <p className="text-dark font-weight-bolder font-size-lg mb-0">
            {stringFormatting(e.land_name)}
          </p>
          {isFrozen(e?.area_status) && (
            <SVG src={ICON_CONSTANTS.FREEZE_STATUS} className="ml-2" />
          )}
          {e?.area_status === LABELING_CONST.BLACKLISTED && (
            <SVG src={ICON_CONSTANTS.BLACKLIST_STATUS} className="ml-2" />
          )}
        </div>
        <span className="text-muted font-weight-bold d-block">{toMeter(e?.land_area)}</span>
      </>
    )
  }
  const uiMitra = (e) => {
    return (
      <>
        <div className="d-flex align-items-start mb-1">
          <p className="text-dark font-weight-bolder font-size-lg mb-0">
            {stringFormatting(e.mitra_name)}
          </p>
          {isFrozen(e?.mitra_status) && (
            <SVG src={ICON_CONSTANTS.FREEZE_STATUS} className="ml-2" />
          )}
          {e?.mitra_status === LABELING_CONST.BLACKLISTED && (
            <SVG src={ICON_CONSTANTS.BLACKLIST_STATUS} className="ml-2" />
          )}
        </div>
        <span className="text-muted font-weight-bold d-block">
          {stringFormatting(e.mitra_phone_number)}
        </span>
      </>
    )
  }
  const uiName = (e) => {
    return (
      <>
        <div className="d-flex align-items-start mb-1">
          <p className="text-dark font-weight-bolder font-size-lg mb-0">
            {stringFormatting(e.farmer_name)}
          </p>
          {isFrozen(e?.farmer_status) && (
            <SVG src={ICON_CONSTANTS.FREEZE_STATUS} className="ml-2" />
          )}
          {e?.farmer_status === LABELING_CONST.BLACKLISTED && (
            <SVG src={ICON_CONSTANTS.BLACKLIST_STATUS} className="ml-2" />
          )}
        </div>
        <span className="text-muted font-weight-bold d-block">
          {stringFormatting(e.farmer_phone_number)}
        </span>
      </>
    )
  }
  const uiPlantType = (e) => {
    return (
      <>
        <p className="text-dark font-weight-bolder mb-1 font-size-lg">
          {stringFormatting(e.plant_type)}
        </p>
        {subTabActive !==
          getKeyFromValue(
            AREA_REGISTERED_ACTIVE_TAB_ENUM.NOT_PLANTED_YET,
            AREA_REGISTERED_ACTIVE_TAB_ENUM
          ) && (
          <span className="text-muted font-weight-bold d-block">
            {stringFormatting(e.variety_name)}
          </span>
        )}
      </>
    )
  }
  const uiActivationDate = (e) => {
    return (
      <>
        <p className="text-dark font-weight-bolder mb-1 font-size-lg">
          {e.activation_date
            ? timeStampToDate(e.activation_date, DATE_TYPE_ENUM.ISO_DATE_STRAP)
            : "-"}
        </p>
      </>
    )
  }
  const uiBACount = (e) => {
    return (
      <div
        className="d-flex align-items-center justify-content-center cursor-pointer"
        style={{ gap: 4 }}
        onClick={() => (e?.ba_count > 0 ? handleOnClickReportDetail(e.land_id) : null)}>
        {e?.ba_count ? (
          <SVG src={toAbsoluteUrl(ICON_CONSTANTS.NOTE_VIEW_WARNING_TRIANGEL)} />
        ) : null}
        <p className={`text-dark mb-1 font-size-lg ${e?.ba_count > 0 ? "underline" : ""}`}>
          {e?.ba_count > 0 ? e.ba_count : "-"}
        </p>
      </div>
    )
  }

  const uiAction = (e) => {
    return (
      <>
        {privileges.indexOf(PRIVILEGE_ENUM.AREA_DETAIL_REGISTERED) > -1 && (
          <OverlayTrigger overlay={<Tooltip id="products-edit-tooltip">{t("detail")}</Tooltip>}>
            <div
              className="btn btn-icon btn-light btn-hover-primary btn-sm mx-3"
              onClick={() => {
                history.push(
                  RoutesConstants.AREA.DETAIL_AREA(AREA_REGISTERED_ENUM.MT_ACTIVE, e.land_id)
                )
              }}>
              <DetailSvgIcon></DetailSvgIcon>
            </div>
          </OverlayTrigger>
        )}
      </>
    )
  }

  const uiPlantingDate = (e) => {
    return (
      <>
        <p className="text-dark font-weight-bolder mb-1 font-size-lg">
          {e.planting_date ? timeStampToDate(e.planting_date, DATE_TYPE_ENUM.ISO_DATE_STRAP) : "-"}
        </p>
        <span className="text-muted font-weight-bold d-block">{getHSTUnit(e.hst)}</span>
      </>
    )
  }

  const uiHarvestDate = (e) => {
    return (
      <>
        <p className="text-dark font-weight-bolder mb-1 font-size-lg">
          {e.harvest_date ? timeStampToDate(e.harvest_date, DATE_TYPE_ENUM.ISO_DATE_STRAP) : "-"}
        </p>
        <span className="text-muted font-weight-bold d-block">{getHSTUnit(e.hst)}</span>
      </>
    )
  }

  const uiEstimatedHarvest = (e) => {
    return (
      <>
        <p className="text-dark font-weight-bolder mb-1 font-size-lg">
          {e.harvest_prediction_date
            ? timeStampToDate(e.harvest_prediction_date, DATE_TYPE_ENUM.ISO_DATE_STRAP)
            : "-"}
        </p>
        <span className="text-muted font-weight-bold d-block">
          {getCountDownToday(e.harvest_count_down_days)}
        </span>
      </>
    )
  }

  const uiEstimatedOnlyHarvest = (e) => {
    return (
      <>
        <p className="text-dark font-weight-bolder mb-1 font-size-lg">
          {e.harvest_prediction_date
            ? timeStampToDate(e.harvest_prediction_date, DATE_TYPE_ENUM.ISO_DATE_STRAP)
            : "-"}
        </p>
      </>
    )
  }

  const uiHarvestTonnage = (e) => {
    return (
      <>
        <p className="text-dark font-weight-bolder mb-1 font-size-lg">
          {getTonnageUnit(e.harvest_tonnage)}
        </p>
      </>
    )
  }

  const uiTotalRevenue = (e) => {
    return (
      <>
        <p className="text-muted mb-1 font-size-lg">{toRupiah(e?.total_revenue)}</p>
      </>
    )
  }

  const getColumnsForTab = (tabKey) => {
    const baseColumns = [
      {
        dataField: "landName",
        text: t("table_header_area_name"),
        formatter: formatterRowComponent,
        formatExtraData: { externalStyle: uiCode },
        sortCaret: sortCaret,
        sort: true,
        onSort: handleSort,
        headerStyle: () => {
          return { width: "17%" }
        }
      },
      {
        dataField: "mitraName",
        text: t("table_header_mitra"),
        formatter: formatterRowComponent,
        formatExtraData: { externalStyle: uiMitra },
        sortCaret: sortCaret,
        sort: true,
        onSort: handleSort,
        headerStyle: () => {
          return { width: "13%" }
        }
      },
      {
        dataField: "farmerName",
        text: t("table_header_farmer"),
        formatter: formatterRowComponent,
        formatExtraData: { externalStyle: uiName },
        sortCaret: sortCaret,
        sort: true,
        onSort: handleSort,
        headerStyle: () => {
          return { width: "13%" }
        }
      },
      {
        dataField: "plantType",
        text: t("table_header_planting_type"),
        formatter: formatterRowComponent,
        formatExtraData: { externalStyle: uiPlantType },
        sortCaret: sortCaret,
        sort: true,
        onSort: handleSort,
        headerStyle: () => {
          return { width: "13%" }
        }
      }
    ]

    // Define dynamic columns
    const activationDateCol = {
      dataField: "activationDate",
      text: t("table_header_activation_date"),
      formatter: formatterRowComponent,
      formatExtraData: { externalStyle: uiActivationDate },
      sortCaret: sortCaret,
      sort: true,
      onSort: handleSort,
      headerStyle: () => {
        return { width: 150 }
      }
    }

    const plantingDateCol = {
      dataField: "plantingDate",
      text: t("table_header_planting_date_short"),
      formatter: formatterRowComponent,
      formatExtraData: { externalStyle: uiPlantingDate },
      sortCaret: sortCaret,
      sort: true,
      onSort: handleSort,
      headerStyle: () => {
        return { width: 150 }
      }
    }

    const harvestDateCol = {
      dataField: "harvestDate",
      text: t("table_header_harvest_date_short"),
      formatter: formatterRowComponent,
      formatExtraData: { externalStyle: uiHarvestDate },
      sortCaret: sortCaret,
      sort: true,
      onSort: handleSort,
      headerStyle: () => {
        return { width: 150 }
      }
    }

    const estimatedHarvestCol = {
      dataField: "harvestPredictionDate",
      text: t("table_header_estimated_harvest"),
      formatter: formatterRowComponent,
      formatExtraData: { externalStyle: uiEstimatedHarvest },
      sortCaret: sortCaret,
      sort: true,
      onSort: handleSort
    }

    const estimatedOnlyHarvestCol = {
      dataField: "harvestPredictionDate",
      text: t("table_header_estimated_harvest"),
      formatter: formatterRowComponent,
      formatExtraData: { externalStyle: uiEstimatedOnlyHarvest },
      sortCaret: sortCaret,
      sort: true,
      onSort: handleSort
    }

    const harvestTonnageCol = {
      dataField: "harvestTonnage",
      text: t("table_header_harvest_tonnage"),
      formatter: formatterRowComponent,
      formatExtraData: { externalStyle: uiHarvestTonnage },
      sortCaret: sortCaret,
      sort: true,
      onSort: handleSort
    }

    const totalRevenueCol = {
      dataField: "totalRevenue",
      text: t("table_header_total_revenue"),
      formatter: formatterRowComponent,
      formatExtraData: { externalStyle: uiTotalRevenue },
      sortCaret: sortCaret,
      sort: true,
      onSort: handleSort
    }

    const baCountCol = {
      dataField: "baCount",
      text: t("table_header_ba_count"),
      formatter: formatterRowComponent,
      formatExtraData: { externalStyle: uiBACount },
      headerStyle: () => ({ width: 100 }),
      sortCaret: sortCaret,
      sort: true,
      onSort: handleSort
    }

    const actionCol = {
      dataField: "action",
      text: "ACTION",
      sort: false,
      formatter: formatterRowComponent,
      formatExtraData: { externalStyle: uiAction },
      headerStyle: () => {
        return { width: 100 }
      }
    }

    const columns = [...baseColumns]

    switch (tabKey) {
      case getKeyFromValue(
        AREA_REGISTERED_ACTIVE_TAB_ENUM.NOT_PLANTED_YET,
        AREA_REGISTERED_ACTIVE_TAB_ENUM
      ):
        columns.push(activationDateCol)
        columns.push(baCountCol)
        columns.push(actionCol)
        break

      case getKeyFromValue(
        AREA_REGISTERED_ACTIVE_TAB_ENUM.ACTIVE_PLANTING,
        AREA_REGISTERED_ACTIVE_TAB_ENUM
      ):
        columns.push(plantingDateCol)
        columns.push(baCountCol)
        columns.push(actionCol)
        break

      case getKeyFromValue(
        AREA_REGISTERED_ACTIVE_TAB_ENUM.UPCOMING_HARVEST,
        AREA_REGISTERED_ACTIVE_TAB_ENUM
      ):
      case getKeyFromValue(
        AREA_REGISTERED_ACTIVE_TAB_ENUM.ANOMALY,
        AREA_REGISTERED_ACTIVE_TAB_ENUM
      ):
        columns.push(plantingDateCol)
        columns.push(estimatedHarvestCol)
        columns.push(baCountCol)
        columns.push(actionCol)
        break

      case getKeyFromValue(
        AREA_REGISTERED_ACTIVE_TAB_ENUM.HARVEST_PERIOD,
        AREA_REGISTERED_ACTIVE_TAB_ENUM
      ):
        columns.push(plantingDateCol)
        columns.push(estimatedOnlyHarvestCol)
        columns.push(baCountCol)
        columns.push(actionCol)
        break

      case getKeyFromValue(
        AREA_REGISTERED_ACTIVE_TAB_ENUM.HARVESTED,
        AREA_REGISTERED_ACTIVE_TAB_ENUM
      ):
        columns.push(harvestDateCol)
        columns.push(harvestTonnageCol)
        columns.push(totalRevenueCol)
        columns.push(baCountCol)
        columns.push(actionCol)
        break

      default:
        columns.push(activationDateCol)
        columns.push(baCountCol)
        columns.push(actionCol)
    }

    return columns
  }

  const handleSort = useCallback(
    (field, order) => {
      let param = getUrlParsingPageFilter({
        ...initialFilterTable,
        tab: subTabActive,
        sort: field + "," + order
      })
      history.replace({ pathname: location.pathname, search: param })
    },
    [subTabActive]
  )

  const columns = useMemo(() => getColumnsForTab(subTabActive), [subTabActive])

  return (
    <>
      {initialActive && (
        <SubMenuFilter
          onClick={(data) => {
            handleChangeSubTab(data)
            setSubTabActive(data)
          }}
          initialActive={AREA_REGISTERED_ACTIVE_TAB_ENUM[initialActive]}
        />
      )}

      {(General.loading && General.actionInitType === AREA.GET_LIST_REGISTERED_V2) ? (
        <TableSkeleton column={7} row={10} />
      ) : data?.pageData.length == 0 ? (
        <EmptyCard
          subtitle={(filterOn || isActiveSearch) && t("messages_response_empty_search_subtitle")}
          image={
            filterOn || isActiveSearch
              ? ILLUSTRATION_CONSTANTS.SEARCH_NO_DATA
              : ILLUSTRATION_CONSTANTS.EMPTY_STATE_NO_DATA
          }
          body={
            filterOn || isActiveSearch
              ? t("messages_response_searching_not_found")
              : t("messages_response_empty_state", {
                  name: t("lahan")
                })
          }
        />
      ) : (
        ""
      )}
      <TableView
        key={`${subTabActive}-${isMultiSelect}`}
        callbackAfterPageChange={changePage}
        columnProperties={columns}
        dataTable={data?.pageData ?? []}
        currentPage={data?.page?.currentPage ?? 1}
        currentElement={data?.page?.currentElement ?? 10}
        totalCount={data?.page?.totalCount ?? 0}
        loadingGetData={general?.loading}
        actionInitType={AREA.GET_LIST_REGISTERED_V2}
        ignoreSinglePage
        rowClasses={() => {
          return "cursor-pointer"
        }}
        keyField="land_id"
        selectable={isMultiSelect}
        onSelect={handleSelect}
        onSelectAll={handleSelectAll}
        selected={selectedIds}
        nonSelectable={nonSelectableIds}
        headerColumnStyle={{ width: 40 }}
      />
      {showModalListBA && selectedAreaId && (
        <ModalListBA
          show={showModalListBA}
          setShow={setShowModalListBA}
          areaId={selectedAreaId}
          key={selectedAreaId}
        />
      )}
    </>
  )
}
